import React, { Fragment } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import Link from '@atlaskit/link/link';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';
import { PageSearchCard } from './plugin-cards/page-search-card';
import { TeamworkDocumentCard } from './plugin-cards/teamwork-document-card';
import { TeamworkTeamCard } from './plugin-cards/teamwork-team-card';
import type { ParsedPluginOutput, PluginExtraInfo } from './types';

const styles = cssMap({
	numberBox: {
		minWidth: '24px',
		flexShrink: 0,
	},
	linkBox: {
		overflow: 'hidden',
		wordBreak: 'break-word',
		flexGrow: 1,
	},
	divider: {
		height: '1px',
		backgroundColor: token('color.background.neutral'),
		marginTop: token('space.150'),
		marginBottom: token('space.150'),
	},
});

type PluginOutputRendererProps = {
	parsedOutput: ParsedPluginOutput | null;
	extraInfo: PluginExtraInfo | string | null | undefined;
};

const TeamworkRenderer = ({ data }: { data: ParsedPluginOutput & { type: 'teamwork' } }) => {
	return (
		<Stack space="space.200">
			{data.data.header && <Text weight="medium">{data.data.header}</Text>}
			{data.data.sections.map((section, sectionIdx) => (
				<Stack key={sectionIdx} space="space.150">
					{section.sectionHeader && (
						<Text weight="medium" size="small">
							{section.sectionHeader}
						</Text>
					)}
					{section.teams && section.teams.length > 0 && (
						<Stack space="space.100">
							{section.teams.map((team, idx) => (
								<TeamworkTeamCard key={idx} team={team} index={idx} />
							))}
						</Stack>
					)}
					{section.documents && section.documents.length > 0 && (
						<Stack space="space.100">
							{section.documents.map((doc, idx) => (
								<TeamworkDocumentCard key={idx} doc={doc} index={idx} />
							))}
						</Stack>
					)}
				</Stack>
			))}
		</Stack>
	);
};

const SearchPluginRenderer = ({ extraInfo }: { extraInfo: PluginExtraInfo }) => {
	const { formatMessage } = useIntl();

	return (
		<Fragment>
			{extraInfo.searchResults && extraInfo.searchResults.length > 0 && (
				<Box>
					<Heading size="xsmall">
						{formatMessage(messages.ai_debugger_modal_search_results_heading, {
							count: extraInfo.searchResults.length,
						})}
					</Heading>
					<Stack space="space.050">
						{extraInfo.searchResults.map((url, idx) => (
							<Inline key={idx} space="space.050" alignBlock="start">
								<Box xcss={styles.numberBox}>
									<Text>{idx + 1}.</Text>
								</Box>
								<Box xcss={styles.linkBox}>
									<Link href={url} target="_blank">
										{url}
									</Link>
								</Box>
							</Inline>
						))}
					</Stack>
				</Box>
			)}
			{extraInfo.searchResults &&
				extraInfo.searchResults.length > 0 &&
				extraInfo.rankedDocuments &&
				extraInfo.rankedDocuments.length > 0 && <Box xcss={styles.divider} />}
			{extraInfo.rankedDocuments && extraInfo.rankedDocuments.length > 0 && (
				<Box>
					<Heading size="xsmall">
						{formatMessage(messages.ai_debugger_modal_ranked_documents_heading, {
							count: extraInfo.rankedDocuments.length,
						})}
					</Heading>
					<Stack space="space.050">
						{extraInfo.rankedDocuments.map((url: string, idx: number) => (
							<Inline key={idx} space="space.050" alignBlock="start">
								<Box xcss={styles.numberBox}>
									<Text>{idx + 1}.</Text>
								</Box>
								<Box xcss={styles.linkBox}>
									<Link href={url} target="_blank">
										{url}
									</Link>
								</Box>
							</Inline>
						))}
					</Stack>
				</Box>
			)}
		</Fragment>
	);
};

export const PluginOutputRenderer = ({
	parsedOutput,
	extraInfo,
}: PluginOutputRendererProps): React.JSX.Element | null => {
	// Render based on parsed output type
	if (parsedOutput) {
		switch (parsedOutput.type) {
			case 'teamwork':
				return <TeamworkRenderer data={parsedOutput} />;
			case 'pageSearch':
				return <PageSearchCard data={parsedOutput.data} />;
		}
	}

	// Fallback to string display
	if (typeof extraInfo === 'string') {
		return <Text>{extraInfo}</Text>;
	}

	// Render search plugin extra info
	if (extraInfo && (extraInfo.searchResults || extraInfo.rankedDocuments)) {
		return <SearchPluginRenderer extraInfo={extraInfo} />;
	}

	return null;
};
